import { useCallback, useEffect, useMemo, useState } from 'react';

import { getStaffPermissions, updateStaffPermissions } from '../../../../util/APIUtils';
import { sortRolePermissionCatalog } from '../../../../v2/modules/create-staff/utils/createStaffFormUtils';
import { resolveEnabledPermissionFilterGroups } from '../../../../v2/modules/create-staff/utils/staffPermissionSections';

/** State and side effects for StaffPermissionsTab: load effective permissions, edit toggles, save. */
function useStaffPermissionsTabHook({
  personId,
  canEdit,
  onSaveSuccess,
  hasActiveLocation = true,
  locationIds = [],
}) {
  const [items, setItems] = useState([]);
  const [toggleState, setToggleState] = useState({});
  const [initialState, setInitialState] = useState({});
  const [loading, setLoading] = useState(() => Boolean(hasActiveLocation));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const locationIdsKey = (locationIds ?? []).join(',');

  const load = useCallback(async () => {
    if (!personId || !hasActiveLocation) return;
    setLoading(true);
    setError(null);
    try {
      const filterGroups = resolveEnabledPermissionFilterGroups({
        locationIds: locationIdsKey === '' ? [] : locationIdsKey.split(','),
      });
      const res = await getStaffPermissions(personId, filterGroups, true);
      const list = sortRolePermissionCatalog(res?.permissions || []);
      setItems(list);
      const next = {};
      list.forEach((p) => {
        next[p.permissionId] = Boolean(p.effectiveGranted);
      });
      setToggleState(next);
      setInitialState(next);
    } catch (e) {
      console.error(e);
      setError('Could not load permissions.');
    } finally {
      setLoading(false);
    }
  }, [personId, hasActiveLocation, locationIdsKey]);

  useEffect(() => {
    if (!hasActiveLocation) {
      setLoading(false);
      setItems([]);
      setToggleState({});
      setInitialState({});
      setError(null);
      setIsEditing(false);
      return;
    }
    load();
  }, [hasActiveLocation, load]);

  useEffect(() => {
    if (!canEdit) {
      setIsEditing(false);
    }
  }, [canEdit]);

  const isDirty = useMemo(
    () => items.some((p) => toggleState[p.permissionId] !== initialState[p.permissionId]),
    [items, toggleState, initialState]
  );

  const showEditChrome = canEdit && isEditing;

  const handleStartEdit = useCallback(() => {
    setError(null);
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setToggleState({ ...initialState });
    setIsEditing(false);
    setError(null);
  }, [initialState]);

  const handleTogglePermission = useCallback((permissionId, enabled) => {
    setToggleState((prev) => ({
      ...prev,
      [permissionId]: enabled,
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!canEdit || !isDirty) return;
    setSaving(true);
    setError(null);
    try {
      const permissions = items.map((p) => ({
        permissionId: p.permissionId,
        enabled: Boolean(toggleState[p.permissionId]),
      }));
      await updateStaffPermissions(personId, { permissions });
      setInitialState({ ...toggleState });
      setIsEditing(false);
      if (onSaveSuccess) onSaveSuccess();
    } catch (e) {
      console.error(e);
      setError('Save failed. You may not have permission to edit these permissions.');
    } finally {
      setSaving(false);
    }
  }, [canEdit, isDirty, items, onSaveSuccess, personId, toggleState]);

  return {
    items,
    toggleState,
    loading,
    saving,
    error,
    isDirty,
    showEditChrome,
    handleStartEdit,
    handleCancelEdit,
    handleSave,
    handleTogglePermission,
  };
}

export default useStaffPermissionsTabHook;
